/**
 * Centralised breadcrumb-trail builders.
 *
 * Every hub / entity / fixture page builds its breadcrumb trail through these
 * helpers so the visible `<Breadcrumbs>` component and the `BreadcrumbList`
 * JSON-LD always share one source of truth. Crucially, each intermediate crumb
 * points at a REAL index route (`/leagues`, `/markets`, `/players`, …) — never
 * a placeholder that would 404. `src/lib/breadcrumbs.test.ts` enforces this
 * against the app's route table so a broken trail can never ship silently.
 */

export interface Crumb {
  name: string;
  path: string;
}

/** Root crumb shared by every trail. */
export const HOME_CRUMB: Crumb = { name: "Home", path: "/" };

/**
 * Guarantee every crumb in a trail has a distinct name (case-insensitive).
 * Search engines flag `BreadcrumbList` items that repeat a name, and a repeated
 * label reads as broken navigation. Keeps the FIRST occurrence of each name and
 * drops later duplicates (the earlier crumb is the higher-level ancestor).
 * Also strips empty names. Shared by the visible trail and the JSON-LD builder
 * so on-page navigation and structured data always match.
 */
export function dedupeCrumbs(items: Crumb[]): Crumb[] {
  const seen = new Set<string>();
  const out: Crumb[] = [];
  for (const it of items) {
    const key = (it.name || "").trim().toLowerCase();
    if (!key || seen.has(key)) continue;
    seen.add(key);
    out.push(it);
  }
  return out;
}

/** League hub: Home › Leagues › {label}. */
export const leagueCrumbs = (slug: string, label: string): Crumb[] => [
  HOME_CRUMB,
  { name: "Leagues", path: "/leagues" },
  { name: label, path: `/leagues/${slug}` },
];

/** Market hub: Home › Markets › {label}. */
export const marketCrumbs = (slug: string, label: string): Crumb[] => [
  HOME_CRUMB,
  { name: "Markets", path: "/markets" },
  { name: label, path: `/markets/${slug}` },
];

/** Competition hub: Home › {country} › {label}. */
export const competitionCrumbs = (comp: {
  slug: string;
  label: string;
  countryLabel: string;
  countrySlug: string;
}): Crumb[] => [
  HOME_CRUMB,
  { name: comp.countryLabel, path: `/countries/${comp.countrySlug}` },
  { name: comp.label, path: `/competitions/${comp.slug}` },
];

/** Country hub: Home › {label}. */
export const countryCrumbs = (slug: string, label: string): Crumb[] => [
  HOME_CRUMB,
  { name: label, path: `/countries/${slug}` },
];

/**
 * Team hub: Home › Teams › {label}. There is no dedicated teams index, so the
 * "Teams" crumb intentionally points at the all-predictions page.
 */
export const teamCrumbs = (slug: string, label: string): Crumb[] => [
  HOME_CRUMB,
  { name: "Teams", path: "/predictions" },
  { name: label, path: `/teams/${slug}` },
];

/**
 * Club hub: Home › Clubs › {label}. No dedicated clubs index, so "Clubs"
 * points at the all-predictions page.
 */
export const clubCrumbs = (slug: string, label: string): Crumb[] => [
  HOME_CRUMB,
  { name: "Clubs", path: "/clubs" },
  { name: label, path: `/clubs/${slug}` },
];

/** Player hub: Home › Players › {label}. */
export const playerCrumbs = (slug: string, label: string): Crumb[] => [
  HOME_CRUMB,
  { name: "Players", path: "/players" },
  { name: label, path: `/players/${slug}` },
];

/** All-predictions hub: Home › Football Predictions. */
export const predictionsCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "Football Predictions", path: "/predictions" },
];

/** AI predictions hub: Home › Football Predictions › AI Predictions. */
export const aiPredictionsCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "Football Predictions", path: "/predictions" },
  { name: "AI Predictions", path: "/ai-predictions" },
];

/** Track-record page: Home › Track Record. */
export const trackRecordCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "Track Record", path: "/track-record" },
];

/** Performance dashboard: Home › Performance. */
export const performanceCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "Performance", path: "/performance" },
];

/** Betting guides index or a specific guide: Home › Betting Guides › {slug}. */
export const guidesCrumbs = (guide?: { slug: string; title: string }): Crumb[] => [
  HOME_CRUMB,
  { name: "Betting Guides", path: "/guides" },
  ...(guide ? [{ name: guide.title, path: `/guides/${guide.slug}` }] : []),
];

/** Blog index or a specific post: Home › Blog › {slug}. */
export const blogCrumbs = (post?: { slug: string; title: string }): Crumb[] => [
  HOME_CRUMB,
  { name: "Blog", path: "/blog" },
  ...(post ? [{ name: post.title, path: `/blog/${post.slug}` }] : []),
];

/** Historical odds archive: Home › Historical Odds. */
export const historicalOddsCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "Historical Odds", path: "/historical-odds" },
];

/** Analysis dashboard: Home › Analysis (optionally scoped to a fixture). */
export const analysisCrumbs = (label?: string): Crumb[] => [
  HOME_CRUMB,
  { name: "Analysis", path: "/analysis" },
  ...(label ? [{ name: label, path: `/analysis?q=${encodeURIComponent(label)}` }] : []),
];


/** Match preview tool: Home › Match Preview. */
export const matchPreviewCrumbs = (label?: string): Crumb[] => [
  HOME_CRUMB,
  { name: label || "Match Preview", path: "/match-preview" },
];

/** High-value odds portal: Home › High Odds Predictions. */
export const highOddsCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "High Odds Predictions", path: "/high-odds" },
];

/** Safe bets portal: Home › Safe Bets. */
export const safeBetsCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "Safe Bets", path: "/safe-bets" },
];

/** Jackpot predictions: Home › Jackpot Predictions. */
export const jackpotCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "Jackpot Predictions", path: "/jackpot-predictions" },
];

/** Bookmaker predictions hub: Home › Bookmakers › {label}. */
export const bookmakerCrumbs = (slug?: string, label?: string): Crumb[] => [
  HOME_CRUMB,
  { name: "Bookmakers", path: "/predictions/bookmaker" },
  ...(slug && label ? [{ name: label, path: `/predictions/bookmaker/${slug}` }] : []),
];

/** Best-prediction-site landing: Home › Best Prediction Site. */
export const bestSiteCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "Best Prediction Site", path: "/best-football-prediction-site" },
];

/** US sports hub: Home › {sport} Predictions. */
export const usSportsCrumbs = (sport: "nfl" | "nba" | "mlb" | "nhl"): Crumb[] => [
  HOME_CRUMB,
  { name: `${sport.toUpperCase()} Predictions`, path: `/${sport}-predictions` },
];

/** MCP docs: Home › Docs › MCP. */
export const mcpDocsCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "Docs", path: "/docs/mcp" },
  { name: "MCP", path: "/docs/mcp" },
];

/** AI access page: Home › AI Access. */
export const aiAccessCrumbs = (): Crumb[] => [
  HOME_CRUMB,
  { name: "AI Access", path: "/ai-access" },
];

/** Homepage (root): a single Home crumb — emitted only as JSON-LD, not visible. */
export const homeCrumbs = (): Crumb[] => [HOME_CRUMB];

/** Generic *sIndex hub crumbs (leagues / markets / countries / competitions / players). */
export const indexCrumbs = (
  kind: "leagues" | "markets" | "countries" | "competitions" | "players",
): Crumb[] => {
  const label: Record<typeof kind, string> = {
    leagues: "Leagues",
    markets: "Markets",
    countries: "Countries",
    competitions: "Competitions",
    players: "Players",
  } as const;
  return [HOME_CRUMB, { name: label[kind], path: `/${kind}` }];
};
